//src/pages/MonitoramentoAdmin.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";

import {
  Activity,
  AlertTriangle,
  Clock3,
  Globe2,
  RefreshCcw,
  Route,
  Server,
  ShieldAlert,
  ShieldCheck,
  Users,
  Wifi
} from "lucide-react";

import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import axios from "../axiosConfig";
import "./MonitoramentoAdmin.css";

function MonitoramentoAdmin() {
  const [dados, setDados] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [atualizando, setAtualizando] =
    useState(false);

  const [erro, setErro] =
    useState("");

  const carregar = useCallback(
    async (silencioso = false) => {
      if (silencioso) {
        setAtualizando(true);
      } else {
        setLoading(true);
      }

      setErro("");

      try {
        const resposta =
          await axios.get(
            "/admin/monitoramento/resumo"
          );

        setDados(
          resposta.data || null
        );
      } catch (err) {
        console.error(
          "Erro ao carregar monitoramento:",
          err
        );

        setErro(
          err.response?.data?.error ||
            "Não foi possível carregar o monitoramento."
        );
      } finally {
        setLoading(false);
        setAtualizando(false);
      }
    },
    []
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  const cards =
    dados?.cards || {};

  const topIps =
    Array.isArray(dados?.top_ips)
      ? dados.top_ips
      : [];

  const topRotas =
    Array.isArray(dados?.top_rotas)
      ? dados.top_rotas
      : [];

  const recentes =
    Array.isArray(dados?.recentes)
      ? dados.recentes
      : [];

  const apiExterna =
    Array.isArray(dados?.api_externa)
      ? dados.api_externa
      : [];

  const serieRequests =
    Array.isArray(
      dados?.serie_requests
    )
      ? dados.serie_requests
      : [];

  const formatarNumero = (valor) =>
    Number(valor || 0).toLocaleString(
      "pt-BR",
      {
        maximumFractionDigits: 0
      }
    );

  const formatarDataHora = (valor) => {
    if (!valor) return "—";

    const data = new Date(valor);

    if (
      Number.isNaN(
        data.getTime()
      )
    ) {
      return "—";
    }

    return data.toLocaleString(
      "pt-BR"
    );
  };

  const formatarHora = (valor) => {
    if (!valor) return "";

    const data = new Date(valor);

    if (
      Number.isNaN(
        data.getTime()
      )
    ) {
      return "";
    }

    return data.toLocaleTimeString(
      "pt-BR",
      {
        hour: "2-digit",
        minute: "2-digit"
      }
    );
  };

  const grafico = useMemo(
    () =>
      serieRequests.map(
        (item) => ({
          ...item,

          label:
            formatarHora(
              item.hora
            ),

          requisicoes:
            Number(
              item.requisicoes ||
                0
            ),

          erros:
            Number(
              item.erros || 0
            )
        })
      ),
    [serieRequests]
  );

  const totalErros =
    Number(
      cards.erros_4xx_24h || 0
    ) +
    Number(
      cards.erros_5xx_24h || 0
    );

  const statusClass = (
    status
  ) => {
    const codigo =
      Number(status || 0);

    if (codigo >= 500) {
      return "danger";
    }

    if (codigo >= 400) {
      return "warning";
    }

    if (codigo >= 200) {
      return "success";
    }

    return "neutral";
  };

  if (loading) {
    return (
      <div className="monitoramentoAdmin-state">
        <RefreshCcw
          size={25}
          className="monitoramentoAdmin-spin"
        />

        <strong>
          Carregando monitoramento...
        </strong>
      </div>
    );
  }

  return (
    <div className="monitoramentoAdmin-page">
      <div className="monitoramentoAdmin-container">

        <section className="monitoramentoAdmin-hero">
          <div>
            <span className="monitoramentoAdmin-kicker">
              Segurança e infraestrutura
            </span>

            <h1>
              Monitoramento Tikpontos
            </h1>

            <p>
              Acompanhe o tráfego recebido pelo
              Tikpontos, comportamento dos
              usuários, erros e as requisições
              realizadas para APIs externas.
            </p>
          </div>

          <button
            type="button"
            className="monitoramentoAdmin-heroBtn"
            onClick={() =>
              carregar(true)
            }
            disabled={atualizando}
          >
            <RefreshCcw
              size={16}
              className={
                atualizando
                  ? "monitoramentoAdmin-spin"
                  : ""
              }
            />

            {atualizando
              ? "Atualizando..."
              : "Atualizar dados"}
          </button>
        </section>

        {erro && (
          <div className="monitoramentoAdmin-error">
            <AlertTriangle
              size={17}
            />

            {erro}
          </div>
        )}

        <section className="monitoramentoAdmin-kpis">

          <article className="monitoramentoAdmin-kpi">
            <div className="monitoramentoAdmin-kpiIcon blue">
              <Activity size={20} />
            </div>

            <span>
              Requisições / 24h
            </span>

            <strong>
              {formatarNumero(
                cards.requisicoes_24h
              )}
            </strong>
          </article>

          <article className="monitoramentoAdmin-kpi">
            <div className="monitoramentoAdmin-kpiIcon cyan">
              <Users size={20} />
            </div>

            <span>
              IPs únicos / 24h
            </span>

            <strong>
              {formatarNumero(
                cards.ips_unicos_24h
              )}
            </strong>
          </article>

          <article className="monitoramentoAdmin-kpi">
            <div className="monitoramentoAdmin-kpiIcon orange">
              <ShieldAlert size={20} />
            </div>

            <span>
              Erros 4xx / 24h
            </span>

            <strong>
              {formatarNumero(
                cards.erros_4xx_24h
              )}
            </strong>
          </article>

          <article
            className={`monitoramentoAdmin-kpi ${
              Number(
                cards.erros_5xx_24h ||
                  0
              ) > 0
                ? "danger"
                : ""
            }`}
          >
            <div className="monitoramentoAdmin-kpiIcon red">
              <AlertTriangle
                size={20}
              />
            </div>

            <span>
              Erros 5xx / 24h
            </span>

            <strong>
              {formatarNumero(
                cards.erros_5xx_24h
              )}
            </strong>
          </article>

          <article className="monitoramentoAdmin-kpi">
            <div className="monitoramentoAdmin-kpiIcon green">
              <Clock3 size={20} />
            </div>

            <span>
              Tempo médio
            </span>

            <strong>
              {formatarNumero(
                cards.duracao_media_ms
              )}
              <small> ms</small>
            </strong>
          </article>

          <article className="monitoramentoAdmin-kpi">
            <div className="monitoramentoAdmin-kpiIcon purple">
              <Server size={20} />
            </div>

            <span>
              API externa / 24h
            </span>

            <strong>
              {formatarNumero(
                cards.api_externa_24h
              )}
            </strong>
          </article>

          <article
            className={`monitoramentoAdmin-kpi ${
              Number(
                cards
                  .api_externa_erros_24h ||
                  0
              ) > 0
                ? "danger"
                : ""
            }`}
          >
            <div className="monitoramentoAdmin-kpiIcon red">
              <Wifi size={20} />
            </div>

            <span>
              Falhas API externa
            </span>

            <strong>
              {formatarNumero(
                cards
                  .api_externa_erros_24h
              )}
            </strong>
          </article>

          <article className="monitoramentoAdmin-kpi">
            <div className="monitoramentoAdmin-kpiIcon blue">
              <ShieldCheck
                size={20}
              />
            </div>

            <span>
              Total de erros
            </span>

            <strong>
              {formatarNumero(
                totalErros
              )}
            </strong>
          </article>
        </section>

        <section className="monitoramentoAdmin-mainGrid">

          <article className="monitoramentoAdmin-panel monitoramentoAdmin-chartPanel">
            <div className="monitoramentoAdmin-panelHeader">
              <div>
                <span>
                  Últimas 24 horas
                </span>

                <h3>
                  Volume de requisições
                </h3>
              </div>

              <Activity size={18} />
            </div>

            {!grafico.length ? (
              <div className="monitoramentoAdmin-emptyChart">
                Ainda não há histórico
                suficiente para o gráfico.
              </div>
            ) : (
              <ResponsiveContainer
                width="100%"
                height={280}
              >
                <AreaChart
                  data={grafico}
                >
                  <defs>
                    <linearGradient
                      id="monitoramentoArea"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#1bb3cd"
                        stopOpacity={0.55}
                      />

                      <stop
                        offset="95%"
                        stopColor="#1bb3cd"
                        stopOpacity={0.04}
                      />
                    </linearGradient>
                  </defs>

                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="label"
                  />

                  <YAxis />

                  <Tooltip />

                  <Area
                    type="monotone"
                    dataKey="requisicoes"
                    name="Requisições"
                    stroke="#1b4fd8"
                    fill="url(#monitoramentoArea)"
                    strokeWidth={3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </article>

          <article className="monitoramentoAdmin-panel">
            <div className="monitoramentoAdmin-panelHeader">
              <div>
                <span>
                  Origem do tráfego
                </span>

                <h3>
                  IPs com mais acessos
                </h3>
              </div>

              <Globe2 size={18} />
            </div>

            <div className="monitoramentoAdmin-ranking">
              {topIps.map(
                (item, index) => (
                  <div
                    className="monitoramentoAdmin-rankingRow"
                    key={
                      item.ip ||
                      index
                    }
                  >
                    <span className="monitoramentoAdmin-rank">
                      {index + 1}
                    </span>

                    <div className="monitoramentoAdmin-rankingMain">
                      <strong>
                        {item.ip || "—"}
                      </strong>

                      <small>
                        {formatarNumero(
                          item.rotas_distintas
                        )}{" "}
                        rota(s) distinta(s)
                      </small>
                    </div>

                    <div className="monitoramentoAdmin-rankingValue">
                      <strong>
                        {formatarNumero(
                          item.requisicoes
                        )}
                      </strong>

                      <small>
                        {formatarNumero(
                          item.erros
                        )}{" "}
                        erro(s)
                      </small>
                    </div>
                  </div>
                )
              )}

              {!topIps.length && (
                <div className="monitoramentoAdmin-empty">
                  Nenhum IP registrado.
                </div>
              )}
            </div>
          </article>

          <article className="monitoramentoAdmin-panel">
            <div className="monitoramentoAdmin-panelHeader">
              <div>
                <span>
                  Utilização
                </span>

                <h3>
                  Rotas mais acessadas
                </h3>
              </div>

              <Route size={18} />
            </div>

            <div className="monitoramentoAdmin-ranking">
              {topRotas.map(
                (item, index) => (
                  <div
                    className="monitoramentoAdmin-rankingRow"
                    key={
                      item.rota ||
                      index
                    }
                  >
                    <span className="monitoramentoAdmin-rank route">
                      {index + 1}
                    </span>

                    <div className="monitoramentoAdmin-rankingMain">
                      <strong
                        title={
                          item.rota
                        }
                      >
                        {item.rota}
                      </strong>

                      <small>
                        média{" "}
                        {formatarNumero(
                          item.duracao_media_ms
                        )}
                        ms
                      </small>
                    </div>

                    <div className="monitoramentoAdmin-rankingValue">
                      <strong>
                        {formatarNumero(
                          item.requisicoes
                        )}
                      </strong>

                      <small>
                        {formatarNumero(
                          item.erros
                        )}{" "}
                        erro(s)
                      </small>
                    </div>
                  </div>
                )
              )}
            </div>
          </article>
        </section>

        <section className="monitoramentoAdmin-panel monitoramentoAdmin-widePanel">
          <div className="monitoramentoAdmin-panelHeader">
            <div>
              <span>
                Integrações
              </span>

              <h3>
                Requisições para APIs externas
              </h3>
            </div>

            <Server size={18} />
          </div>

          <div className="monitoramentoAdmin-tableWrap">
            <table className="monitoramentoAdmin-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Serviço</th>
                  <th>Origem</th>
                  <th>Método</th>
                  <th>Endpoint</th>
                  <th>Status</th>
                  <th>Tempo</th>
                </tr>
              </thead>

              <tbody>
                {!apiExterna.length ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="monitoramentoAdmin-empty"
                    >
                      Nenhuma chamada externa
                      registrada.
                    </td>
                  </tr>
                ) : (
                  apiExterna.map(
                    (item) => (
                      <tr key={item.id}>
                        <td>
                          {formatarDataHora(
                            item.criado_em
                          )}
                        </td>

                        <td>
                          <strong>
                            {item.servico}
                          </strong>
                        </td>

                        <td>
                          {item.origem ||
                            "—"}
                        </td>

                        <td>
                          <span className="monitoramentoAdmin-method">
                            {item.metodo}
                          </span>
                        </td>

                        <td
                          className="monitoramentoAdmin-route"
                          title={
                            item.endpoint
                          }
                        >
                          {item.endpoint}
                        </td>

                        <td>
                          <span
                            className={`monitoramentoAdmin-status ${statusClass(
                              item.status_http
                            )}`}
                          >
                            {item.status_http ||
                              "ERRO"}
                          </span>
                        </td>

                        <td>
                          {formatarNumero(
                            item.duracao_ms
                          )}
                          ms
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="monitoramentoAdmin-panel monitoramentoAdmin-widePanel">
          <div className="monitoramentoAdmin-panelHeader">
            <div>
              <span>
                Tráfego recebido
              </span>

              <h3>
                Requisições recentes
              </h3>
            </div>

            <Globe2 size={18} />
          </div>

          <div className="monitoramentoAdmin-tableWrap">
            <table className="monitoramentoAdmin-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Método</th>
                  <th>Rota</th>
                  <th>Status</th>
                  <th>Tempo</th>
                  <th>IP</th>
                  <th>Usuário</th>
                  <th>Tipo</th>
                </tr>
              </thead>

              <tbody>
                {!recentes.length ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="monitoramentoAdmin-empty"
                    >
                      Nenhuma requisição
                      registrada.
                    </td>
                  </tr>
                ) : (
                  recentes.map(
                    (item) => (
                      <tr key={item.id}>
                        <td>
                          {formatarDataHora(
                            item.criado_em
                          )}
                        </td>

                        <td>
                          <span className="monitoramentoAdmin-method">
                            {item.metodo}
                          </span>
                        </td>

                        <td
                          className="monitoramentoAdmin-route"
                          title={
                            item.rota
                          }
                        >
                          {item.rota}
                        </td>

                        <td>
                          <span
                            className={`monitoramentoAdmin-status ${statusClass(
                              item.status_http
                            )}`}
                          >
                            {item.status_http ||
                                                           "—"}
                          </span>
                        </td>

                        <td>
                          {formatarNumero(
                            item.duracao_ms
                          )}
                          ms
                        </td>

                        <td>
                          <span className="monitoramentoAdmin-ip">
                            {item.ip || "—"}
                          </span>
                        </td>

                        <td>
                          {item.usuario_id
                            ? `#${item.usuario_id}`
                            : "Não autenticado"}
                        </td>

                        <td>
                          <span
                            className={`monitoramentoAdmin-userType ${
                              item.usuario_tipo ||
                              "anonimo"
                            }`}
                          >
                            {item.usuario_tipo ||
                              "anônimo"}
                          </span>
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="monitoramentoAdmin-healthGrid">
          <article
            className={`monitoramentoAdmin-healthCard ${
              Number(
                cards.erros_5xx_24h ||
                  0
              ) === 0
                ? "ok"
                : "danger"
            }`}
          >
            <div>
              {Number(
                cards.erros_5xx_24h ||
                  0
              ) === 0 ? (
                <ShieldCheck
                  size={21}
                />
              ) : (
                <ShieldAlert
                  size={21}
                />
              )}
            </div>

            <span>
              Saúde da aplicação
            </span>

            <strong>
              {Number(
                cards.erros_5xx_24h ||
                  0
              ) === 0
                ? "Normal"
                : "Atenção"}
            </strong>

            <small>
              {Number(
                cards.erros_5xx_24h ||
                  0
              ) === 0
                ? "Nenhum erro 5xx registrado nas últimas 24 horas."
                : `${formatarNumero(
                    cards.erros_5xx_24h
                  )} erro(s) 5xx registrado(s) nas últimas 24 horas.`}
            </small>
          </article>

          <article
            className={`monitoramentoAdmin-healthCard ${
              Number(
                cards
                  .api_externa_erros_24h ||
                  0
              ) === 0
                ? "ok"
                : "danger"
            }`}
          >
            <div>
              {Number(
                cards
                  .api_externa_erros_24h ||
                  0
              ) === 0 ? (
                <Server size={21} />
              ) : (
                <AlertTriangle
                  size={21}
                />
              )}
            </div>

            <span>
              Integrações externas
            </span>

            <strong>
              {Number(
                cards
                  .api_externa_erros_24h ||
                  0
              ) === 0
                ? "Normal"
                : "Atenção"}
            </strong>

            <small>
              {Number(
                cards
                  .api_externa_erros_24h ||
                  0
              ) === 0
                ? "Nenhuma falha de API externa registrada nas últimas 24 horas."
                : `${formatarNumero(
                    cards
                      .api_externa_erros_24h
                  )} falha(s) externa(s) registrada(s) nas últimas 24 horas.`}
            </small>
          </article>

          <article className="monitoramentoAdmin-healthCard info">
            <div>
              <Globe2 size={21} />
            </div>

            <span>
              Diversidade de acesso
            </span>

            <strong>
              {formatarNumero(
                cards.ips_unicos_24h
              )} IPs
            </strong>

            <small>
              Endereços IP distintos
              registrados nas últimas
              24 horas.
            </small>
          </article>

          <article className="monitoramentoAdmin-healthCard info">
            <div>
              <Clock3 size={21} />
            </div>

            <span>
              Performance média
            </span>

            <strong>
              {formatarNumero(
                cards.duracao_media_ms
              )}
              ms
            </strong>

            <small>
              Tempo médio das
              requisições recebidas
              nas últimas 24 horas.
            </small>
          </article>
        </section>

      </div>
    </div>
  );
}

export default MonitoramentoAdmin;